// A product's RBP links (RBP-D33, SL1.0 `a=rbp_links`): its children in the selected catalogue,
// shown with the same cards, availability, price and on-order composition as a browse page, so
// they appear "as browse lists them" (an off-sale child as the not-available card). Figma has no
// design for it (OQ-5): the product page's shell, the listing's grid and its view toggle.
//
// A caller outside the RBP scope gets a 404 from the read, shown as the empty state: the page
// does not exist for them.
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { Banner, Button, Icon } from '../../design-system';
import { fetchRbpLinks, type RbpLinks } from '../../api/rbp-links.client';
import { useAvailability } from '../../availability/useAvailability';
import { usePrices } from '../../pricing/usePriceStore';
import { useMultibuyDeals } from '../../multibuy/useMultibuyDealsStore';
import { useOnOrder } from '../../onOrder/useOnOrder';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { useSessionStore } from '../../auth/useSessionStore';
import { isAdminRole } from '../../auth/rbac';
import type { RbpLinksHandover } from '../../rbp/rbpLinksRoute';
import { CatalogueLinkingHeader } from '../Products/CatalogueLinkingHeader';
import { ProductCard } from '../Browse/ProductCard';
import { useViewMode } from '../Browse/useViewMode';
import listing from '../Browse/CategoryListing.module.css';
import shell from '../ProductDetail/ProductDetail.module.css';
import styles from './RbpLinksPage.module.css';

/** The first row of cards loads its images eagerly, as a browse page's does. */
const EAGER_IMAGE_COUNT = 4;

type Phase =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error' }
  | { readonly kind: 'ready'; readonly links: RbpLinks | null };

export const NO_RBP_LINKS_MESSAGE = 'No RBP links for this product.';

export function RbpLinksPage() {
  const { sku = '' } = useParams<{ sku: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const role = useSessionStore((s) => s.role);
  const entryKey = useCatalogueEntryKey();
  const handover = location.state as RbpLinksHandover | null;
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const { viewMode, selectView } = useViewMode();

  useEffect(() => {
    // With no entry there is nothing to ask yet: the ordering-choice gate supplies one.
    if (entryKey === null || sku === '') return;
    let current = true;
    setPhase({ kind: 'loading' });
    fetchRbpLinks(entryKey, sku)
      .then((links) => {
        if (current) setPhase({ kind: 'ready', links });
      })
      .catch(() => {
        if (current) setPhase({ kind: 'error' });
      });
    return () => {
      current = false;
    };
  }, [entryKey, sku, attempt]);

  const products = phase.kind === 'ready' ? (phase.links?.products ?? []) : [];
  const skus = products.map((product) => product.sku);
  useAvailability(skus);
  usePrices(skus);
  // Pricing migration 32 (ADO-3985): the children's multi-buy deals, as browse reads them for its
  // cards (D-5: no-op unless VITE_REAL_PRICES_ENABLED is on).
  useMultibuyDeals(skus);
  useOnOrder(skus);

  const parentName =
    handover?.parentName ?? (phase.kind === 'ready' ? phase.links?.parent?.name : undefined);

  return (
    <div className={shell.page}>
      <CatalogueLinkingHeader showManagerDashboard={isAdminRole(role)} />
      <main className={shell.main} aria-label="RBP links">
        <div className={shell.content}>
          <Button
            variant="secondary"
            className={styles.back}
            // Opened directly (a new tab, a shared link) there is no in-app page to go back to.
            onClick={() => void (location.key === 'default' ? navigate('/products') : navigate(-1))}
          >
            Back
          </Button>
          <div className={styles.toolbar}>
            <h1 className={styles.heading}>
              {parentName === undefined ? 'RBP links' : `RBP links for ${parentName}`}
            </h1>
            <div className={listing.viewToggle} role="group" aria-label="View mode">
              <button
                type="button"
                className={`${listing.viewButton} ${viewMode === 'grid' ? listing.viewButtonActive : ''}`}
                aria-label="Grid view"
                aria-pressed={viewMode === 'grid'}
                onClick={() => selectView('grid')}
              >
                <Icon name="gridView" size={20} />
              </button>
              <button
                type="button"
                className={`${listing.viewButton} ${viewMode === 'list' ? listing.viewButtonActive : ''}`}
                aria-label="List view"
                aria-pressed={viewMode === 'list'}
                onClick={() => selectView('list')}
              >
                <Icon name="listView" size={20} />
              </button>
            </div>
          </div>

          {phase.kind === 'loading' && (
            <p className={listing.status} role="status">
              Loading RBP links…
            </p>
          )}
          {phase.kind === 'error' && (
            <Banner type="error">
              Couldn&rsquo;t load the RBP links.{' '}
              <Button variant="secondary" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </Button>
            </Banner>
          )}
          {phase.kind === 'ready' && products.length === 0 && (
            <div className={listing.empty} role="status">
              <p className={listing.emptyTitle}>{NO_RBP_LINKS_MESSAGE}</p>
            </div>
          )}
          {products.length > 0 && (
            <ul className={viewMode === 'list' ? listing.list : listing.grid}>
              {products.map((product, index) => (
                <li key={product.id}>
                  <ProductCard
                    product={product}
                    priority={index < EAGER_IMAGE_COUNT}
                    layout={viewMode}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
    </div>
  );
}
